import * as THREE from 'three';
import * as TWEEN from '@tweenjs/tween.js';

export class MovementManager {
    constructor(camera, pod) {
        this.camera = camera;
        this.pod = pod;
        this.currentMovement = null;
        this.movements = {
            dunyawi: {
                starSpeed: 3,
                cameraPath: this.createForwardPath.bind(this),
                duration: 30000
            },
            briolissens: {
                starSpeed: 0.5,
                cameraPath: this.createOrbitalPath.bind(this),
                duration: 45000 // Longer duration for orbital movement
            }
        };
    }

    startMovement(type, onProgress) {
        if (this.currentMovement) {
            this.stopMovement();
        }

        const movement = this.movements[type];
        if (!movement) return;

        this.currentMovement = type;
        const curve = movement.cameraPath();
        
        // Create a progress value for tweening
        const progress = { value: 0 };
        
        // Show content after half a second
        if (onProgress) {
            setTimeout(onProgress, 1000);
        }
        
        // Create the tween
        new TWEEN.Tween(progress)
            .to({ value: 1 }, movement.duration)
            .easing(TWEEN.Easing.Sinusoidal.InOut)
            .onUpdate(() => {
                const point = curve.getPoint(progress.value);
                this.camera.position.copy(point);
                
                if (this.pod) {
                    this.pod.position.copy(point);
                    
                    // Rotate pod based on movement type
                    if (type === 'briolissens') {
                        // Calculate tangent for smooth pod rotation
                        const tangent = curve.getTangent(progress.value);
                        this.pod.lookAt(point.x + tangent.x, point.y + tangent.y, point.z + tangent.z);
                    } else {
                        // Reset rotation for other movements
                        this.pod.rotation.set(0, 0, 0);
                    }
                }
            })
            .repeat(type === 'briolissens' ? Infinity : 0) // Only repeat orbital movement
            .start();
    }

    stopMovement() {
        TWEEN.removeAll();
        this.currentMovement = null;
    }

    getCurrentMovementType() {
        return this.currentMovement;
    }

    getStarSpeedForMovement(type) {
        return this.movements[type]?.starSpeed || 0;
    }

    createForwardPath() {
        const curve = new THREE.LineCurve3(
            new THREE.Vector3(0, 0, 5),
            new THREE.Vector3(0, 0, -50)
        );
        return curve;
    }

    createOrbitalPath() {
        const points = [];
        const radius = 15; // Smaller radius for tighter orbit
        const segments = 128; // More segments for smoother curve
        const verticalOffset = 5; // Keep pod in view
        const verticalAmplitude = 3; // Height of the wave
        const verticalCycles = 2; // Number of up/down cycles per orbit

        for (let i = 0; i <= segments; i++) {
            const theta = (i / segments) * Math.PI * 2;
            const verticalWave = Math.sin(theta * verticalCycles) * verticalAmplitude;
            
            points.push(new THREE.Vector3(
                Math.cos(theta) * radius,
                verticalWave, // Add vertical movement
                Math.sin(theta) * radius + verticalOffset
            ));
        }

        const curve = new THREE.CatmullRomCurve3(points, true);
        return curve;
    }

    update() {
        TWEEN.update();
    }
}
